import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, LogOut, Plus, Trash2, Star, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, CATEGORIES, CATEGORY_NAMES, relativeTime, exactTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { TrustBadge } from "@/components/TrustBadge";
import AdminLogin from "@/pages/AdminLogin";
import { Registry, ReviewQueue, BrokenLinks, AuditLog, SettingsPanel } from "@/pages/AdminCompliance";
import { AdsPanel, TrafficPanel } from "@/pages/AdminAds";
import { SourcesPanel } from "@/pages/AdminSources";
import { SocialSourcesPanel } from "@/pages/AdminSocial";

const TABS = ["Overview", "Registry", "Review Queue", "Articles", "Flagged", "Sources", "Social Sources", "Clusters", "Broken Links", "Submissions", "Audit", "Logs", "Ads", "Traffic", "Settings"];

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("Overview");
  const [fetching, setFetching] = useState(false);

  if (loading) return <div className="pt-40 text-center text-muted-foreground">Loading…</div>;
  if (!admin) return <AdminLogin />;

  const fetchNews = async () => {
    setFetching(true);
    toast.info("Fetching latest news from all active sources…");
    try {
      const res = (await api.post("/admin/fetch")).data;
      toast.success(`Done — ${res.new_articles} new, ${res.duplicates} duplicates across ${res.sources_checked} sources`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3"><Logo size={34} /><span className="hidden sm:inline text-sm font-medium text-slate-400">/ Admin</span></div>
          <div className="flex items-center gap-2">
            <button data-testid="btn-fetch-latest-news" onClick={fetchNews} disabled={fetching} className="inline-flex items-center gap-2 bg-violet-600 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-violet-700 transition-colors disabled:opacity-60">
              {fetching ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Fetch Latest News
            </button>
            <button onClick={() => { logout(); navigate("/"); }} className="p-2 text-slate-500 hover:text-rose-600" title="Log out"><LogOut size={18} /></button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t}</button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {tab === "Overview" && <Overview />}
        {tab === "Articles" && <Articles />}
        {tab === "Flagged" && <Flagged />}
        {tab === "Sources" && <SourcesPanel />}
        {tab === "Social Sources" && <SocialSourcesPanel />}
        {tab === "Clusters" && <Clusters />}
        {tab === "Submissions" && <Submissions />}
        {tab === "Logs" && <Logs />}
        {tab === "Registry" && <Registry />}
        {tab === "Review Queue" && <ReviewQueue />}
        {tab === "Broken Links" && <BrokenLinks />}
        {tab === "Audit" && <AuditLog />}
        {tab === "Settings" && <SettingsPanel />}
        {tab === "Ads" && <AdsPanel />}
        {tab === "Traffic" && <TrafficPanel />}
      </main>
    </div>
  );
}

function Overview() {
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const cards = [
    { label: "Total Articles", value: data?.total_articles, },
    { label: "Published (permitted fields)", value: data?.verified },
    { label: "Pending Review", value: data?.pending },
    { label: "Discovered Today", value: data?.articles_today },
    { label: "Active Sources", value: data?.active_sources },
    { label: "Event Clusters", value: data?.clusters },
    { label: "Needs Review", value: data?.review_required },
    { label: "Broken Links", value: data?.broken_links },
    { label: "Sources Approved", value: data?.sources_by_status?.approved },
    { label: "Sources Restricted", value: data?.sources_by_status?.restricted },
    { label: "Sources Pending", value: data?.sources_by_status?.pending_review },
    { label: "Sources Blocked", value: data?.sources_by_status?.blocked },
  ];
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-5">Discovery & Compliance Overview</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid="overview-card" className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="font-display font-extrabold text-3xl text-violet-700">{c.value ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-sm text-muted-foreground">Use <strong>Fetch Latest News</strong> to manually pull from all active sources. The platform also polls sources automatically in the background.</p>
    </div>
  );
}

function Articles() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["admin-articles", status], queryFn: async () => (await api.get(`/admin/articles?limit=50${status ? `&status=${status}` : ""}`)).data });

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["admin-articles"] }); } catch { toast.error("Action failed"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-bold text-2xl tracking-tight">Articles</h1>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
          <option value="">All statuses</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="removed">Removed</option>
        </select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.items?.map((a) => (
          <div key={a.id} data-testid="admin-article-row" className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <TrustBadge status={a.status} />
                {a.featured && <span className="text-[10px] font-mono text-amber-600 inline-flex items-center gap-0.5"><Star size={10} /> Featured</span>}
                <span className="font-mono text-[10px] text-violet-600">{(a.categories || []).map((c) => CATEGORY_NAMES[c]).join(", ")}</span>
              </div>
              <div className="text-sm font-medium text-slate-800 line-clamp-1">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{a.source_name} · {relativeTime(a.published_at)}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {a.status !== "verified" && <IconBtn testid="approve-article" title="Approve" onClick={() => act(() => api.patch(`/admin/articles/${a.id}/status`, { status: "verified" }), "Approved")}><CheckCircle2 size={16} className="text-emerald-600" /></IconBtn>}
              {a.status !== "removed" && <IconBtn testid="remove-article" title="Remove" onClick={() => act(() => api.patch(`/admin/articles/${a.id}/status`, { status: "removed" }), "Removed")}><XCircle size={16} className="text-rose-600" /></IconBtn>}
              <IconBtn testid="feature-article" title="Feature" onClick={() => act(() => api.patch(`/admin/articles/${a.id}/feature`), "Toggled feature")}><Star size={16} className={a.featured ? "text-amber-500 fill-amber-400" : "text-slate-400"} /></IconBtn>
            </div>
          </div>
        ))}
        {!data?.items?.length && <div className="p-8 text-center text-sm text-muted-foreground">No articles.</div>}
      </div>
    </div>
  );
}

function Sources() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-sources"], queryFn: async () => (await api.get("/admin/sources")).data });
  const [form, setForm] = useState({ name: "", rss_url: "", homepage: "", region: "Global", priority: 5, ai_only_feed: false });
  const [adding, setAdding] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-sources"] });

  const addSource = async (e) => {
    e.preventDefault();
    setAdding(true);
    try { await api.post("/admin/sources", form); toast.success("Source added"); setForm({ name: "", rss_url: "", homepage: "", region: "Global", priority: 5, ai_only_feed: false }); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };
  const toggle = async (s, field) => { try { await api.patch(`/admin/sources/${s.id}`, { [field]: !s[field] }); refresh(); } catch { toast.error("Failed"); } };
  const del = async (s) => { if (!window.confirm(`Block and disable ${s.name}? Records are retained for audit.`)) return; try { await api.delete(`/admin/sources/${s.id}`); toast.success("Blocked & disabled"); refresh(); } catch { toast.error("Failed"); } };
  const fetchOne = async (s) => { toast.info(`Fetching ${s.name}…`); try { const r = (await api.post(`/admin/fetch?source_id=${s.id}`)).data; toast.success(`+${r.new_articles} new`); qc.invalidateQueries(); } catch { toast.error("Failed"); } };

  const inp = "px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-violet-300";

  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Sources</h1>
      <form onSubmit={addSource} className="bg-white rounded-xl border border-slate-200 p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input data-testid="source-name-input" required placeholder="Source name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
        <input data-testid="source-rss-input" required placeholder="RSS feed URL" value={form.rss_url} onChange={(e) => setForm({ ...form, rss_url: e.target.value })} className={inp} />
        <input placeholder="Homepage (optional)" value={form.homepage} onChange={(e) => setForm({ ...form, homepage: e.target.value })} className={inp} />
        <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={inp}>
          {["Global", "North America", "Europe", "Middle East", "Asia", "Other"].map((r) => <option key={r}>{r}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.ai_only_feed} onChange={(e) => setForm({ ...form, ai_only_feed: e.target.checked })} /> AI-only feed (skip keyword filter)</label>
        <button data-testid="add-source-btn" disabled={adding} className="inline-flex items-center justify-center gap-1.5 bg-violet-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-60"><Plus size={15} /> Add Source</button>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.map((s) => (
          <div key={s.id} data-testid="admin-source-row" className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800">{s.name} <span className="font-normal text-xs text-muted-foreground">· {s.region} · P{s.priority} · {s.eligibility_status}</span></div>
              <div className="text-xs text-muted-foreground truncate">{s.rss_url}</div>
              {s.last_fetched_at && <div className="text-[11px] text-slate-400 mt-0.5">Last fetched {relativeTime(s.last_fetched_at)}</div>}
            </div>
            <div className="flex items-center gap-3 shrink-0 text-xs">
              <Toggle label="Enabled" on={s.enabled} onClick={() => toggle(s, "enabled")} testid="toggle-source-enabled" />
              <Toggle label="Auto-publish" on={s.auto_publish} onClick={() => toggle(s, "auto_publish")} testid="toggle-source-autopublish" />
              <button onClick={() => fetchOne(s)} className="p-1.5 rounded hover:bg-slate-100 text-violet-600" title="Fetch now"><RefreshCw size={15} /></button>
              <button onClick={() => del(s)} className="p-1.5 rounded hover:bg-slate-100 text-rose-500" title="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Clusters() {
  const { data } = useQuery({ queryKey: ["admin-clusters"], queryFn: async () => (await api.get("/admin/clusters")).data });
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Duplicate / Event Clusters</h1>
      <p className="text-sm text-muted-foreground mb-4">Stories about the same event, grouped so the feed isn't flooded with duplicates.</p>
      <div className="space-y-4">
        {data?.map((c) => (
          <div key={c.cluster_id} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs font-mono text-violet-600 mb-2">{c.count} sources · same event</div>
            <div className="divide-y divide-slate-100">
              {c.articles.map((a) => (
                <div key={a.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="text-sm text-slate-700 line-clamp-1">{a.title}</div>
                  <span className="text-xs text-muted-foreground shrink-0">{a.source_name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!data?.length && <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-muted-foreground">No multi-source clusters yet.</div>}
      </div>
    </div>
  );
}

function Flagged() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-flagged"], queryFn: async () => (await api.get("/admin/flagged")).data });
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["admin-flagged"] }); } catch { toast.error("Action failed"); } };
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Flagged for Review</h1>
      <p className="text-sm text-muted-foreground mb-4">Stories that failed one or more editorial/copyright screening checks. Screening is a tool, not a legal judgment — review before acting.</p>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.map((a) => (
          <div key={a.id} data-testid="flagged-row" className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 line-clamp-1">{a.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.source_name} · {relativeTime(a.published_at)}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {a.status !== "verified" && <IconBtn testid="approve-flagged" title="Approve" onClick={() => act(() => api.patch(`/admin/articles/${a.id}/status`, { status: "verified" }), "Approved")}><CheckCircle2 size={16} className="text-emerald-600" /></IconBtn>}
                <IconBtn testid="remove-flagged" title="Remove" onClick={() => act(() => api.patch(`/admin/articles/${a.id}/status`, { status: "removed" }), "Removed")}><XCircle size={16} className="text-rose-600" /></IconBtn>
              </div>
            </div>
            {a.validation?.flag_reasons?.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {a.validation.flag_reasons.map((r, i) => (
                  <li key={i} className="text-[11px] font-mono text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{r}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {!data?.length && <div className="p-8 text-center text-sm text-muted-foreground">No flagged stories.</div>}
      </div>
    </div>
  );
}

function Submissions() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-submissions"], queryFn: async () => (await api.get("/admin/submissions")).data });
  const resolve = async (id) => { try { await api.patch(`/admin/submissions/${id}/resolve`); toast.success("Marked resolved"); qc.invalidateQueries({ queryKey: ["admin-submissions"] }); } catch { toast.error("Failed"); } };
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Contact, Corrections, Reports & Rights Requests</h1>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.map((s) => (
          <div key={s.id} data-testid="submission-row" className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${s.kind === "copyright" || s.type === "rights_issue" ? "bg-rose-50 text-rose-700" : "bg-violet-50 text-violet-700"}`}>{s.kind}{s.kind === "report" ? ` · ${s.type}` : ""}</span>
                {s.status === "resolved" && <span className="text-[10px] font-mono text-emerald-600">resolved</span>}
                <span className="text-xs text-muted-foreground">{relativeTime(s.created_at)}</span>
              </div>
              <div className="text-sm font-medium text-slate-800">{s.name || s.reporter_name || "Anonymous"} · <span className="text-slate-500 font-normal">{s.email || s.reporter_email || "no email"}</span></div>
              {s.kind === "report" && s.article_url && <div className="text-xs text-muted-foreground truncate">{s.article_url}</div>}
              {s.rights_holder && <div className="text-xs text-muted-foreground">Rights holder: {s.rights_holder} · {s.article_url}</div>}
              <div className="text-sm text-slate-600 mt-1">{s.message || s.description}</div>
            </div>
            {s.status !== "resolved" && <button onClick={() => resolve(s.id)} className="shrink-0 text-xs font-medium text-violet-700 hover:text-violet-900">Mark resolved</button>}
          </div>
        ))}
        {!data?.length && <div className="p-8 text-center text-sm text-muted-foreground">No submissions yet.</div>}
      </div>
    </div>
  );
}

function Logs() {
  const { data } = useQuery({ queryKey: ["admin-logs"], queryFn: async () => (await api.get("/admin/logs")).data });
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">Retrieval Logs</h1>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data?.map((l) => (
          <div key={l.id} className="p-3.5 flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <span className={`font-medium ${l.status === "success" ? "text-emerald-600" : "text-rose-600"}`}>{l.status}</span>
              <span className="text-slate-700 ml-2">{l.source_name}</span>
              <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">{l.message}</div>
            </div>
            <span className="text-xs text-muted-foreground shrink-0" title={exactTime(l.started_at)}>{relativeTime(l.started_at)}</span>
          </div>
        ))}
        {!data?.length && <div className="p-8 text-center text-sm text-muted-foreground">No logs yet.</div>}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return <button data-testid={testid} onClick={onClick} title={title} className="p-1.5 rounded hover:bg-slate-100 transition-colors">{children}</button>;
}

function Toggle({ label, on, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-1.5">
      <span className={`w-8 h-4.5 rounded-full transition-colors relative ${on ? "bg-violet-600" : "bg-slate-300"}`} style={{ height: 18 }}>
        <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-all ${on ? "left-4" : "left-0.5"}`} />
      </span>
      <span className="text-slate-600">{label}</span>
    </button>
  );
}
